import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { SERVICE_WORKER_UPDATE_AVAILABLE_EVENT } from 'serviceWorkerRegistration';
import { clearServiceWorkerUpdatePending, setServiceWorkerUpdatePending } from 'services/Utilities/serviceWorkerUpdateGuard';

const useStyles = makeStyles(() => ({
  dialogPaper: {
    maxWidth: 560,
    width: 'calc(100% - 32px)',
    borderRadius: 0,
  },
  title: {
    margin: 0,
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  divider: {
    marginTop: 16,
  },
  body: {
    paddingTop: 24,
    paddingBottom: 8,
  },
  message: {
    fontSize: 18,
    lineHeight: 1.6,
  },
  actions: {
    padding: '0 24px 24px',
    justifyContent: 'center',
  },
  refreshButton: {
    minWidth: 220,
    minHeight: 48,
    borderRadius: 0,
    color: 'black',
    fontWeight: 700,
  },
}));

const NewVersionModal = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [waitingWorker, setWaitingWorker] = useState(null);
  const shouldReloadOnControllerChangeRef = useRef(false);

  const handleRefresh = () => {
    if (waitingWorker) {
      shouldReloadOnControllerChangeRef.current = true;
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator) || window.self !== window.top) return;
    const onUpdateAvailable = event => {
      const registration = event && event.detail ? event.detail.registration : null;
      const nextWaitingWorker = registration ? registration.waiting : null;

      if (!nextWaitingWorker) return;

      setServiceWorkerUpdatePending(true);
      setWaitingWorker(nextWaitingWorker);
    };

    const onControllerChange = () => {
      if (!shouldReloadOnControllerChangeRef.current) return;

      clearServiceWorkerUpdatePending();
      window.location.reload();
    };

    window.addEventListener(SERVICE_WORKER_UPDATE_AVAILABLE_EVENT, onUpdateAvailable);
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      window.removeEventListener(SERVICE_WORKER_UPDATE_AVAILABLE_EVENT, onUpdateAvailable);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  return (
    <Dialog
      open={!!waitingWorker}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
      PaperProps={{ className: classes.dialogPaper }}
      aria-labelledby="new-version-dialog-title"
      aria-describedby="new-version-dialog-description">
      <DialogTitle id="new-version-dialog-title">
        <Typography component="h2" className={classes.title}>
          {t('newVersionAvailable')}
        </Typography>
        <Divider className={classes.divider} />
      </DialogTitle>
      <DialogContent id="new-version-dialog-description" className={classes.body}>
        <Typography className={classes.message}>{t('newVersionAvailableMessage')}</Typography>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button color="secondary" variant="contained" className={classes.refreshButton} onClick={handleRefresh}>
          {t('refresh')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewVersionModal;
